// Right-click (long-press on a phone) on an `RfV(...)` line in the Message Box:
// set the sensor up from the line that named it, without leaving the console.
//
//   NAMED sensor            → RfSensorMenu, the same menu its tile has
//   UNNAMED (auto-created)  → "Set up this sensor…" (RfSensorDialog — name, type,
//                             room, each code's meaning) or "Scan it…"
//   NEW (no row yet)        → "Scan it…" only — there is nothing to edit yet
//
// The receiver is the line's own unique number, so a scan listens to that
// receiver only. Every save invalidates the RF directory, so the line under the
// cursor changes from "Unnamed sensor" to its name without a reload.

import React, { useState } from 'react';
import {
  Menu, MenuItem, ListItemIcon, ListItemText, Divider,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SensorsIcon from '@mui/icons-material/Sensors';
import { getRfDevices } from '../../core/api/api';
import { invalidateRfDirectory } from '../../core/hooks/useRfDirectory';
import { meaningText } from '../../core/devices/rfIdentify';
import RfSensorMenu from './RfSensorMenu';
import RfSensorDialog from './RfSensorDialog';
import RfScanDialog from './RfScanDialog';

const fetchAll = () => getRfDevices({})
  .then((r) => (Array.isArray(r.data) ? r.data : []))
  .catch(() => []);

/**
 * @param target  { rf, receiverUno, x, y } — rf is describeRfCode()'s result — or null
 * @param onClose close the menu
 */
export default function RfLineMenu({ target, onClose }) {
  const [editing, setEditing] = useState(null);
  const [scan, setScan] = useState(null);       // { receiverUno, locationId, devices }

  const rf = target?.rf || null;
  const named = rf?.status === 'named';

  const openScan = async () => {
    const t = target;
    onClose();
    setScan({
      receiverUno: t.receiverUno || '',
      locationId: Number(t.rf?.device?.LocationID) || 0,
      devices: await fetchAll(),
    });
  };

  // A scan saves codes + name; then the full settings for recipients and a test.
  const afterScan = async ({ RfDeviceId, transmitterId }) => {
    invalidateRfDirectory();
    const list = await fetchAll();
    const found = list.find((d) => (RfDeviceId && Number(d.RfDeviceId) === RfDeviceId)
      || Number(d.TransmitterId) === transmitterId);
    if (found) setEditing(found);
  };

  const setUp = () => { const d = rf.device; onClose(); setEditing(d); };

  return (
    <>
      {named ? (
        <RfSensorMenu
          target={target ? { device: rf.device, x: target.x, y: target.y } : null}
          onClose={onClose}
          onSettings={setEditing}
          onChanged={invalidateRfDirectory}
        />
      ) : (
        <Menu
          open={!!target}
          onClose={onClose}
          anchorReference="anchorPosition"
          anchorPosition={target ? { top: target.y, left: target.x } : undefined}
          onContextMenu={(e) => { e.preventDefault(); onClose(); }}
        >
          <MenuItem disabled sx={{ opacity: '0.75 !important' }}>
            <ListItemText
              primary={rf ? `${rf.status === 'new' ? 'New sensor' : 'Unnamed sensor'}${rf.rfNumber ? ` ${rf.rfNumber}` : ''}` : ''}
              secondary={rf ? `hw ${rf.transmitterId} · ${meaningText(rf)} · code ${rf.code}` : ''}
              primaryTypographyProps={{ fontWeight: 700, variant: 'body2' }}
            />
          </MenuItem>
          <Divider />
          {rf?.device && (
            <MenuItem onClick={setUp}>
              <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Set up this sensor…" secondary="Name, type, room, what each code means" />
            </MenuItem>
          )}
          <MenuItem onClick={openScan}>
            <ListItemIcon><SensorsIcon fontSize="small" /></ListItemIcon>
            <ListItemText
              primary="Scan it…"
              secondary={target?.receiverUno ? `Guided: open / close, on receiver ${target.receiverUno}` : 'Guided: open / close'}
            />
          </MenuItem>
        </Menu>
      )}

      <RfScanDialog
        open={!!scan}
        receiverUno={scan?.receiverUno || ''}
        locationId={scan?.locationId || 0}
        devices={scan?.devices || []}
        onClose={() => setScan(null)}
        onSaved={afterScan}
      />
      {/* Rooms are fetched by the dialog from the sensor's own location. */}
      <RfSensorDialog
        device={editing}
        locationId={Number(editing?.LocationID) || 0}
        onClose={() => setEditing(null)}
        onSaved={invalidateRfDirectory}
      />
    </>
  );
}
